import React from 'react';

import useCrossProductUrlWrapper from '@atlassiansox/analytics-cross-product-interaction-client/use-cross-product-url-wrapper';
import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import ArrowLeft from '@atlaskit/icon/core/arrow-left';
import CrossIcon from '@atlaskit/icon/core/cross';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';
import { PanelAction } from '@atlassian/panel-system/panel-action';
import { PanelActionClose } from '@atlassian/panel-system/panel-action/close';
import { PanelActionGroup } from '@atlassian/panel-system/panel-action/group';
import { PanelHeader } from '@atlassian/panel-system/panel-header';

import messages from '../../common/messages';
import { PreviewPanelNewTabLink } from '../preview-panel-action-variants/preview-panel-new-tab-link';
import type { PreviewPanelHeaderProps } from './types';

/**
 * Removes the specified query parameter from a URL while preserving all other parameters
 * @param url - The original URL
 * @param paramToRemove - The query parameter name to remove
 * @returns The URL with the specified parameter removed
 *
 * @private
 * @deprecated `@atlassian/preview-panel` is deprecated. Use `@atlassian/panel-system` instead.
 * {@link https://atlassian.design/components/panel-system/examples}
 */
export const removeQueryParam = (url: string, paramToRemove: string): string => {
	try {
		const urlObj = new URL(url);
		urlObj.searchParams.delete(paramToRemove);
		return urlObj.toString();
	} catch {
		// If URL parsing fails, return the original URL
		return url;
	}
};

const styles = cssMap({
	header: {
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		borderBlockEndStyle: 'solid',
		borderBlockEndWidth: token('border.width'),
		borderColor: token('color.border'),
		height: '56px', //there's no token for this height :(
	},
	childWrapper: {
		flexGrow: 1,
	},
	titleRegion: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		flexGrow: 1,
		minWidth: '0px',
	},
});

/**
 * @private
 * @deprecated `@atlassian/preview-panel` is deprecated. Use `@atlassian/panel-system` instead.
 * {@link https://atlassian.design/components/panel-system/examples}
 */
export const PreviewPanelHeader = ({
	children,
	onClose,
	onOpenInNewTab,
	onNavigateBack,
	testId,
	url,
	analyticsClient,
	parentProduct,
	panelType,
	moreActions,
	spacing,
}: PreviewPanelHeaderProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const enableCrossMAUUsage = fg('platform_preview_panels_cross_app_interaction');

	const buildUrlWithAnalyticsParams = useCrossProductUrlWrapper({
		bridge: 'preview-panels',
		product: parentProduct ?? 'unknown',
	});

	const onPanelClose = () => {
		analyticsClient?.sendTrackEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'preview-panel-close',
			source: 'preview-panel',
			attributes: {
				parentProduct,
				panelType,
			},
		});
		onClose?.();
	};

	const onOpenInNewWindow = () => {
		analyticsClient?.sendTrackEvent({
			action: 'clicked',
			actionSubject: 'link',
			actionSubjectId: 'preview-panel-open-in-new-window',
			source: 'preview-panel',
			attributes: {
				parentProduct,
				panelType,
			},
		});
		onOpenInNewTab?.();
		if (url) {
			window.open(
				enableCrossMAUUsage
					? buildUrlWithAnalyticsParams(removeQueryParam(url, 'previewPanels'))
					: removeQueryParam(url, 'previewPanels'),
			);
		}
	};

	const onPanelNavigateBack = () => {
		analyticsClient?.sendTrackEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'preview-panel-navigate-back',
			source: 'preview-panel',
			attributes: {
				parentProduct,
				panelType,
			},
		});
		onNavigateBack?.();
	};

	if (fg('ads-panel-system-gpp')) {
		return (
			<PanelHeader testId={testId}>
				{onNavigateBack && fg('jivtech-1920-back-navigation-gpp') && (
					<PanelAction
						icon={ArrowLeft}
						label={formatMessage(messages.previewPanelNavigateBackLabel)}
						onClick={onPanelNavigateBack}
						testId={testId && `${testId}.navigate-back-button`}
						appearance="default"
					/>
				)}
				<Box xcss={styles.titleRegion}>{children}</Box>
				<PanelActionGroup>
					{url && (
						<PanelAction
							icon={LinkExternalIcon}
							label={formatMessage(messages.previewPanelOpenInNewWindowLabel)}
							onClick={onOpenInNewWindow}
							testId={testId && `${testId}.open-in-new-window-button`}
							appearance="subtle"
						/>
					)}
					{moreActions}
					{onClose && (
						<PanelActionClose
							onClick={onPanelClose}
							testId={testId && `${testId}.close-button`}
							label={formatMessage(messages.previewPanelCloseButtonLabel)}
						/>
					)}
				</PanelActionGroup>
			</PanelHeader>
		);
	}

	return (
		<Inline xcss={styles.header} testId={testId} alignBlock="center" space="space.100">
			{onNavigateBack && fg('jivtech-1920-back-navigation-gpp') && (
				<IconButton
					icon={(iconProps) => <ArrowLeft {...iconProps} size="small" />}
					label={formatMessage(messages.previewPanelNavigateBackLabel)}
					onClick={onPanelNavigateBack}
					testId={testId && `${testId}.navigate-back-button`}
				/>
			)}
			<Inline
				xcss={styles.childWrapper}
				spread="space-between"
				alignBlock="center"
				space="space.100"
			>
				{children}
			</Inline>
			<Flex gap="space.100">
				{url && (
					<PreviewPanelNewTabLink
						label={formatMessage(messages.previewPanelOpenInNewWindowLabel)}
						onClick={onOpenInNewWindow}
						testId={testId && `${testId}.open-in-new-window-button`}
					/>
				)}
				{moreActions}
				{onClose && (
					<IconButton
						{...(spacing === 'compact' ? { appearance: 'subtle', spacing } : {})}
						icon={(iconProps) => (
							<CrossIcon {...iconProps} size={spacing === 'compact' ? 'medium' : 'small'} />
						)}
						label={formatMessage(messages.previewPanelCloseButtonLabel)}
						onClick={onPanelClose}
						testId={testId && `${testId}.close-button`}
						role="button"
					/>
				)}
			</Flex>
		</Inline>
	);
};
